import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, Eye, Pencil, RemoveFormatting } from "lucide-react";
import { RichText } from "../RichText";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const COLORS = [
    { label: "Encre", value: "#1C2925" },
    { label: "Vert forêt", value: "#164B3B" },
    { label: "Doré champagne", value: "#BE9956" },
    { label: "Doré foncé", value: "#8a6d3b" },
    { label: "Encre douce", value: "#55655E" },
];

const SIZES = [
    { label: "Petit", value: "0.875rem" },
    { label: "Normal", value: "1rem" },
    { label: "Grand", value: "1.15rem" },
    { label: "Très grand", value: "1.35rem" },
];

const BLOCKS = [
    { label: "Paragraphe", value: "p" },
    { label: "Titre H1", value: "h1" },
    { label: "Titre H2", value: "h2" },
    { label: "Titre H3", value: "h3" },
];

function ToolButton({ onAction, title, testid, children }) {
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            data-testid={testid}
            onMouseDown={(e) => {
                e.preventDefault();
                onAction();
            }}
            className="p-1.5 rounded-lg text-pineneedle hover:bg-pearl hover:text-forest transition-colors"
        >
            {children}
        </button>
    );
}

export function RichTextEditor({ value, onChange, testid = "rich-editor", minHeight = "140px" }) {
    const ref = useRef(null);
    const [preview, setPreview] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkBlank, setLinkBlank] = useState(true);
    const savedRange = useRef(null);

    useEffect(() => {
        if (ref.current && ref.current.innerHTML !== (value || "") && document.activeElement !== ref.current) {
            ref.current.innerHTML = value || "";
        }
    }, [value]);

    const focus = () => ref.current?.focus();
    const exec = (cmd, arg = null) => {
        focus();
        document.execCommand("styleWithCSS", false, true);
        document.execCommand(cmd, false, arg);
        emit();
    };
    const emit = () => onChange(ref.current ? ref.current.innerHTML : "");

    const applySpan = (prop, val) => {
        focus();
        const sel = window.getSelection();
        if (!sel.rangeCount || sel.isCollapsed || !ref.current.contains(sel.anchorNode)) return;
        const range = sel.getRangeAt(0);
        const span = document.createElement("span");
        span.style[prop] = val;
        try {
            range.surroundContents(span);
        } catch {
            span.appendChild(range.extractContents());
            range.insertNode(span);
        }
        sel.removeAllRanges();
        emit();
    };

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel.rangeCount && ref.current?.contains(sel.anchorNode)) savedRange.current = sel.getRangeAt(0).cloneRange();
    };

    const insertLink = () => {
        if (!linkUrl) return;
        const url = /^(https?:\/\/|mailto:)/i.test(linkUrl) ? linkUrl : `https://${linkUrl}`;
        focus();
        if (savedRange.current) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedRange.current);
        }
        document.execCommand("createLink", false, url);
        ref.current.querySelectorAll("a").forEach((a) => {
            if (!a.getAttribute("rel")) {
                a.setAttribute("rel", "noopener noreferrer");
                if (linkBlank) a.setAttribute("target", "_blank");
            }
        });
        setLinkOpen(false);
        setLinkUrl("");
        emit();
    };

    return (
        <div className="rounded-xl border border-input bg-white overflow-hidden" data-testid={testid}>
            <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-ivory/60 px-2 py-1.5">
                <select
                    className="h-8 rounded-lg border border-input bg-white px-2 text-xs text-ink mr-1"
                    defaultValue="p"
                    onChange={(e) => exec("formatBlock", e.target.value)}
                    data-testid={`${testid}-block`}
                    aria-label="Niveau de titre"
                >
                    {BLOCKS.map((b) => (
                        <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                </select>
                <ToolButton onAction={() => exec("bold")} title="Gras" testid={`${testid}-bold`}><Bold size={15} /></ToolButton>
                <ToolButton onAction={() => exec("italic")} title="Italique" testid={`${testid}-italic`}><Italic size={15} /></ToolButton>
                <ToolButton onAction={() => exec("underline")} title="Souligné" testid={`${testid}-underline`}><Underline size={15} /></ToolButton>
                <span className="w-px h-5 bg-border mx-1" />
                <select
                    className="h-8 rounded-lg border border-input bg-white px-2 text-xs text-ink"
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) applySpan("fontSize", e.target.value); e.target.value = ""; }}
                    data-testid={`${testid}-size`}
                    aria-label="Taille de police"
                >
                    <option value="" disabled>Taille</option>
                    {SIZES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
                <div className="flex items-center gap-0.5 ml-1" role="group" aria-label="Couleur du texte">
                    {COLORS.map((c) => (
                        <button
                            key={c.value}
                            type="button"
                            title={c.label}
                            aria-label={`Couleur ${c.label}`}
                            data-testid={`${testid}-color-${c.value.replace("#", "")}`}
                            onMouseDown={(e) => { e.preventDefault(); applySpan("color", c.value); }}
                            className="w-5 h-5 rounded-full border border-black/10 hover:scale-110 transition-transform"
                            style={{ backgroundColor: c.value }}
                        />
                    ))}
                </div>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolButton onAction={() => exec("insertUnorderedList")} title="Liste à puces" testid={`${testid}-ul`}><List size={15} /></ToolButton>
                <ToolButton onAction={() => exec("insertOrderedList")} title="Liste numérotée" testid={`${testid}-ol`}><ListOrdered size={15} /></ToolButton>
                <ToolButton
                    onAction={() => { saveSelection(); setLinkOpen(!linkOpen); }}
                    title="Insérer un lien"
                    testid={`${testid}-link`}
                >
                    <Link2 size={15} />
                </ToolButton>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolButton onAction={() => exec("justifyLeft")} title="Aligner à gauche" testid={`${testid}-align-left`}><AlignLeft size={15} /></ToolButton>
                <ToolButton onAction={() => exec("justifyCenter")} title="Centrer" testid={`${testid}-align-center`}><AlignCenter size={15} /></ToolButton>
                <ToolButton onAction={() => exec("justifyRight")} title="Aligner à droite" testid={`${testid}-align-right`}><AlignRight size={15} /></ToolButton>
                <ToolButton onAction={() => exec("removeFormat")} title="Retirer la mise en forme" testid={`${testid}-clear`}><RemoveFormatting size={15} /></ToolButton>
                <span className="w-px h-5 bg-border mx-1" />
                <ToolButton onAction={() => exec("undo")} title="Annuler" testid={`${testid}-undo`}><Undo2 size={15} /></ToolButton>
                <ToolButton onAction={() => exec("redo")} title="Rétablir" testid={`${testid}-redo`}><Redo2 size={15} /></ToolButton>
                <div className="flex-1" />
                <ToolButton onAction={() => setPreview(!preview)} title={preview ? "Revenir à l'édition" : "Aperçu du rendu public"} testid={`${testid}-preview-toggle`}>
                    {preview ? <Pencil size={15} /> : <Eye size={15} />}
                </ToolButton>
            </div>
            {linkOpen && (
                <div className="flex flex-wrap items-end gap-2 border-b border-border bg-pearl/60 px-3 py-2" data-testid={`${testid}-link-form`}>
                    <div className="flex-1 min-w-48">
                        <Label className="text-xs">URL du lien (https://…)</Label>
                        <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" className="h-8 text-xs bg-white" data-testid={`${testid}-link-url`} />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-pineneedle pb-2">
                        <input type="checkbox" checked={linkBlank} onChange={(e) => setLinkBlank(e.target.checked)} data-testid={`${testid}-link-blank`} /> Nouvel onglet
                    </label>
                    <Button size="sm" className="h-8 text-xs bg-forest text-cream" onClick={insertLink} data-testid={`${testid}-link-apply`}>Insérer</Button>
                </div>
            )}
            {preview ? (
                <div className="p-4 bg-ivory/40" data-testid={`${testid}-preview`}>
                    <RichText html={value} className="text-ink-soft leading-relaxed" />
                    {!value && <p className="text-xs text-pineneedle/60">Aucun contenu.</p>}
                </div>
            ) : (
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emit}
                    onBlur={saveSelection}
                    className="rich-text px-4 py-3 text-sm text-ink leading-relaxed outline-none focus:ring-1 focus:ring-ring"
                    style={{ minHeight }}
                    data-testid={`${testid}-area`}
                    role="textbox"
                    aria-multiline="true"
                />
            )}
            <p className="px-3 py-1.5 text-[10px] text-pineneedle/60 border-t border-border bg-ivory/40">
                Mise en forme sécurisée : le contenu est assaini côté serveur avant publication.
            </p>
        </div>
    );
}
